<template>
  <div v-if="firebaseConfigured && rows.length" class="leaderboard">
    <button
      type="button"
      class="leaderboard__head"
      :aria-expanded="open ? 'true' : 'false'"
      @click="open = !open"
    >
      <span class="leaderboard__title">High scores</span>
      <span class="leaderboard__leader">{{ leaderLine }}</span>
    </button>

    <div v-if="open" class="leaderboard__body">
      <div class="leaderboard__tabs" role="tablist">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          role="tab"
          :aria-selected="by === tab.key ? 'true' : 'false'"
          :class="{ 'is-selected': by === tab.key }"
          @click="by = tab.key"
        >{{ tab.label }}</button>
      </div>

      <ol class="leaderboard__rows">
        <li
          v-for="row in ranked"
          :key="row.uid"
          :class="{ 'is-me': row.uid === myUid }"
        >
          <span class="leaderboard__rank">{{ row.rank }}</span>
          <span class="leaderboard__name">{{ row.name }}</span>
          <span class="leaderboard__value">{{ valueFor(row) }}</span>
        </li>
      </ol>

      <p class="leaderboard__note">
        Everyone who has finished a game here. One row each, updated as you play.
      </p>
    </div>
  </div>
</template>

<script>
// The high-score table. Matt, 2026-08-30: "it'd be cool also if we could do,
// like, a high scores table on each of these across people who play it.
// Although for now, it's just me, but it'd be cool if other people ever did."
//
// That last part is the design constraint: this has to look right with ONE
// row in it and still look right with twenty. So it is collapsed to a single
// line naming whoever is on top - which, with one player, reads as a personal
// best rather than a lonely table.
//
// Reads `<game>Leaderboard`, the deliberately public node - one small row per
// player, so the whole board is a single cheap read. The full per-player
// record stays private at `<game>UserStats`.
import { mapState } from 'vuex';
import { readBoard, rankBoard } from '@/store/userStats';

export default {
  name: 'LeaderBoard',
  data() {
    return { rows: [], open: false, by: 'best' };
  },
  computed: {
    ...mapState(['firebaseConfigured']),
    myUid() { return this.$store.getters.myUid; },
    tabs() {
      return [
        { key: 'best', label: 'Best haul' },
        { key: 'wins', label: 'Wins' },
      ];
    },
    ranked() { return rankBoard(this.rows, { by: this.by }); },
    leaderLine() {
      const top = rankBoard(this.rows, { by: 'best' })[0];
      if (!top) return '';
      return top.uid === this.myUid
        ? `Your best ${top.bestScore}`
        : `${top.name} ${top.bestScore}`;
    },
  },
  watch: {
    myUid: { handler: 'load', immediate: true },
  },
  methods: {
    valueFor(row) {
      return this.by === 'best' ? `${row.bestScore}` : `${row.wins || 0}`;
    },
    async load() {
      if (!this.myUid) { this.rows = []; return; }
      this.rows = await readBoard();
    },
  },
};
</script>

<style lang="scss" scoped>
.leaderboard {
  border: 1px solid var(--edge);
  border-radius: var(--radius);
  background: var(--surface);
  padding: 0.7rem 0.85rem;
}

.leaderboard__head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  min-height: 0;
  padding: 0;
  text-align: left;
  width: 100%;
}

.leaderboard__title { color: var(--ink); font-size: 0.9rem; font-weight: 700; }
.leaderboard__leader { color: var(--idol); font-size: 0.85rem; font-weight: 700; }

.leaderboard__body { margin-top: 0.7rem; padding-top: 0.6rem; border-top: 1px solid var(--edge); }

.leaderboard__tabs {
  display: flex;
  gap: 0.4rem;
  margin-bottom: 0.6rem;

  button {
    border: 1px solid var(--edge);
    border-radius: 999px;
    color: var(--ink-dim);
    font-size: 0.75rem;
    min-height: 0;
    padding: 0.25rem 0.7rem;

    &.is-selected { background: var(--idol); border-color: var(--idol); color: #211a05; font-weight: 700; }
  }
}

.leaderboard__rows {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    align-items: baseline;
    display: flex;
    gap: 0.6rem;
    padding: 0.22rem 0;

    &.is-me .leaderboard__name { color: var(--idol); font-weight: 700; }
  }
}

.leaderboard__rank { color: var(--ink-dim); font-size: 0.78rem; min-width: 1.2rem; }
.leaderboard__name { color: var(--ink); flex: 1; font-size: 0.82rem; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.leaderboard__value { color: var(--ink); font-size: 0.82rem; font-weight: 700; }

.leaderboard__note { color: var(--ink-dim); font-size: 0.72rem; margin: 0.6rem 0 0; }
</style>
